import { useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { MapPin, Package, Boxes, Wrench, Building2, FileText, ShieldCheck, CalendarClock, Trophy, Briefcase } from "lucide-react";
import { Badge, Verified } from "../components/ui/kit";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { Stars, RatingSummary, ReviewList } from "../components/Reviews";
import { peso } from "../data/mock";
import { api } from "../lib/api";

export default function Profile() {
  const [, params] = useRoute("/u/:id");
  const id = Number(params?.id);
  const [data, setData] = useState<any>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound">("loading");

  useEffect(() => {
    let alive = true;
    setState("loading");
    api.getProfile(id)
      .then((d) => { if (!alive) return; if (d?.user) { setData(d); setState("ready"); } else setState("notfound"); })
      .catch(() => { if (alive) setState("notfound"); });
    return () => { alive = false; };
  }, [id]);

  if (state === "loading") return <DetailLoading label="Loading profile…" />;
  if (state === "notfound" || !data) return <DetailNotFound title="Profile not found" />;

  const { user, rating, reviews, listings, counts } = data;
  const memberSince = user.memberSince ? new Date(user.memberSince).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "";

  return (
    <div className="container-x py-6">
      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* LEFT — identity */}
        <div className="space-y-4">
          <div className="card p-5 text-center">
            <div className="w-20 h-20 rounded-full bg-navy text-white grid place-items-center text-2xl font-extrabold mx-auto">{user.initials}</div>
            <h1 className="text-xl font-extrabold text-navy mt-3 flex items-center justify-center gap-2">
              {user.name}
              {user.verified && <Verified label={user.verifiedKind === "company" ? "Verified Company" : "Verified ID"} />}
            </h1>
            {data.score && (
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className={`chip ${data.score.tier === "A" ? "bg-emerald-100 text-emerald-700" : data.score.tier === "B" ? "bg-amber-100 text-amber-700" : "bg-navy-50 text-navy-600"}`}><Trophy className="w-3 h-3" />Tier {data.score.tier}</span>
                <span className="text-xs text-mut">Score {data.score.finalScore}/100</span>
              </div>
            )}
            {rating.count > 0 ? (
              <div className="flex items-center justify-center gap-1 mt-1"><Stars value={rating.average} size={16} /><b className="text-navy">{rating.average.toFixed(1)}</b><span className="text-mut text-sm">({rating.count})</span></div>
            ) : <p className="text-sm text-mut mt-1">No reviews yet</p>}
            {memberSince && <p className="text-xs text-mut mt-2">Member since {memberSince}</p>}
            <div className="flex justify-center gap-4 mt-4 pt-4 border-t border-line text-center">
              <div><div className="font-bold text-navy">{counts.items}</div><div className="text-[11px] text-mut">Items</div></div>
              <div><div className="font-bold text-navy">{counts.services}</div><div className="text-[11px] text-mut">Services</div></div>
              <div><div className="font-bold text-navy">{counts.companies}</div><div className="text-[11px] text-mut">Companies</div></div>
              <div><div className="font-bold text-navy">{rating.count}</div><div className="text-[11px] text-mut">Reviews</div></div>
            </div>
          </div>
          {user.verified && (
            <div className="card p-4 flex items-start gap-2 text-xs text-mut">
              <ShieldCheck className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
              <span>This {user.verifiedKind === "company" ? "company" : "member"} has been verified by KontrataPH.</span>
            </div>
          )}
          {user.workerVerified && (
            <div className="card p-4 flex items-start gap-2 text-xs text-mut">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span>Verified worker — identity checked by KontrataPH.</span>
            </div>
          )}
          {data.availability && (
            <div className="card p-4">
              {(() => {
                const st: Record<string, [string, string]> = { available: ["Available for work", "bg-emerald-100 text-emerald-700"], busy: ["Currently busy", "bg-amber-100 text-amber-700"], unavailable: ["Not available", "bg-navy-50 text-mut"] };
                const [label, cls] = st[data.availability.status] || st.available;
                const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                return (
                  <>
                    <div className="flex items-center gap-2 mb-2"><CalendarClock className="w-4 h-4 text-navy" /><span className={`chip ${cls}`}>{label}</span></div>
                    {data.availability.slots.length > 0 && (
                      <div className="space-y-1 text-xs text-mut">
                        {data.availability.slots.map((s: any) => (
                          <div key={s.id} className="flex justify-between"><span className="font-semibold text-navy-700">{DAYS[s.dayOfWeek]}</span><span>{s.startTime}–{s.endTime}</span></div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* RIGHT — reputation + offerings */}
        <div className="space-y-4">
          <div className="card p-5">
            <h2 className="font-bold text-navy mb-3">Reputation</h2>
            {rating.count > 0 ? <div className="mb-4"><RatingSummary average={rating.average} count={rating.count} breakdown={rating.breakdown} /></div> : null}
            <ReviewList reviews={reviews} emptyText="No reviews yet." />
          </div>

          {listings.items.length > 0 && (
            <div className="card p-5">
              <h2 className="font-bold text-navy mb-3 flex items-center gap-2"><Package className="w-4 h-4" />Items &amp; Equipment</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {listings.items.map((it: any) => (
                  <Link key={it.id} href={`/item/${it.id}`} className="flex gap-3 border border-line rounded-lg p-2 hover:border-navy-500">
                    <div className="w-14 h-14 rounded bg-navy-50 grid place-items-center overflow-hidden shrink-0">
                      {it.imageUrl ? <img src={it.imageUrl} alt="" className="w-full h-full object-cover" /> : (it.kind === "equipment" ? <Wrench className="w-5 h-5 text-mut" /> : <Boxes className="w-5 h-5 text-mut" />)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-navy truncate">{it.title}</div>
                      <div className="text-xs text-mut">{peso(it.price)}{it.mode === "rent" ? `/${it.unit || "day"}` : ""}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {listings.services.length > 0 && (
            <div className="card p-5">
              <h2 className="font-bold text-navy mb-3 flex items-center gap-2"><Wrench className="w-4 h-4" />Services</h2>
              <div className="space-y-2">
                {listings.services.map((s: any) => (
                  <Link key={s.id} href={`/s/${s.id}`} className="flex items-center justify-between border border-line rounded-lg p-3 hover:border-navy-500">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-navy truncate flex items-center gap-1">{s.title}{s.verified && <Verified />}</div>
                      <div className="text-xs text-mut flex items-center gap-1"><MapPin className="w-3 h-3" />{s.city}</div>
                    </div>
                    <span className="text-sm font-bold text-navy shrink-0">{peso(s.fromPrice)}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {listings.companies.length > 0 && (
            <div className="card p-5">
              <h2 className="font-bold text-navy mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" />Companies</h2>
              <div className="space-y-2">
                {listings.companies.map((c: any) => (
                  <Link key={c.id} href={`/c/${c.id}`} className="flex items-center gap-3 border border-line rounded-lg p-3 hover:border-navy-500">
                    <div className="w-10 h-10 rounded-lg bg-navy text-white grid place-items-center text-xs font-bold shrink-0">{c.initials}</div>
                    <div className="min-w-0"><div className="text-sm font-semibold text-navy truncate flex items-center gap-1">{c.name}{c.verified && <Verified />}</div><div className="text-xs text-mut truncate">{c.tagline}</div></div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {listings.people.length > 0 && (
            <div className="card p-5">
              <h2 className="font-bold text-navy mb-3 flex items-center gap-2"><FileText className="w-4 h-4" />Résumé</h2>
              <div className="space-y-2">
                {listings.people.map((p: any) => (
                  <Link key={p.id} href={`/p/${p.id}`} className="flex items-center justify-between border border-line rounded-lg p-3 hover:border-navy-500">
                    <div className="min-w-0"><div className="text-sm font-semibold text-navy truncate">{p.title}</div><div className="text-xs text-mut flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}</div></div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* QA #10/#11: posted jobs now appear on the profile. */}
          {listings.jobs?.length > 0 && (
            <div className="card p-5">
              <h2 className="font-bold text-navy mb-3 flex items-center gap-2"><Briefcase className="w-4 h-4" />Jobs Posted</h2>
              <div className="space-y-2">
                {listings.jobs.map((j: any) => (
                  <Link key={j.id} href={`/j/${j.id}`} className="flex items-center justify-between border border-line rounded-lg p-3 hover:border-navy-500">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-navy truncate">{j.title}</div>
                      <div className="text-xs text-mut flex items-center gap-1"><MapPin className="w-3 h-3" />{j.city}{j.type ? ` · ${j.type}` : ""}</div>
                    </div>
                    {j.salary && <span className="text-sm font-bold text-navy shrink-0">{j.salary}</span>}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {counts.items + counts.services + counts.companies + counts.people + (counts.jobs || 0) === 0 && (
            <div className="card p-8 text-center text-sm text-mut">No public listings yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
